/**
 * The single artifact file renderer, shared by the full-page viewer, the
 * embedded panel and the chat surface host.
 *
 * This merges two renderers that had drifted apart: this one (rich media and
 * Office previews, re-signed URL support) and surface-host/artifact-viewer.tsx
 * (CSV/TSV grid, pretty-printed JSON, a much wider set of code file types).
 * Both capability sets are preserved here.
 */

import * as Sentry from '@sentry/react';
import { Download, FileWarning, Loader2 } from 'lucide-react';
import Papa from 'papaparse';
import { Suspense, lazy, useEffect, useMemo, useState } from 'react';

import { MarkdownRenderer } from '@/components/markdown';
import { Button } from '@/components/ui/button';
import {
  PowerPointDocumentDisplay,
  WordDocumentDisplay,
  ExcelDocumentDisplay,
} from '@/pages/workflow/component/display-components';
import VideoPlayer from '@/pages/workflow/component/video-player';
import AudioPlayer from '@/pages/workflow/component/audio-player';
import type { ArtifactDetailResponse, ArtifactFile } from '@/types/artifacts';
import type { NodeFile } from '@/types/node-types';
import {
  VIDEO_EXTENSIONS,
  AUDIO_EXTENSIONS,
  IMAGE_EXTENSIONS,
  PRESENTATION_EXTENSIONS,
  DOCUMENT_EXTENSIONS,
} from '@/types/source-types';
import { AGENT_HTML_IFRAME_SANDBOX } from '@/constants/iframe-sandbox';
import { isSafeFetchUrl } from '@/utils/url-utils';
import { toSameOriginWorkflowUrl } from '@/utils/workflow-file-url';

const CsvPreviewGrid = lazy(
  () => import('@/features/data-sources/csv-preview-grid'),
);

// ---------------------------------------------------------------------------
// Categorisation
// ---------------------------------------------------------------------------

type FileCategory =
  | 'image'
  | 'video'
  | 'audio'
  | 'office-ppt'
  | 'office-doc'
  | 'office-xls'
  | 'pdf'
  | 'html'
  | 'markdown'
  | 'csv'
  | 'json'
  | 'text'
  | 'none';

type ArtifactTypeHint = ArtifactDetailResponse['type'] | undefined;

const EXCEL_EXTENSIONS = ['xls', 'xlsx'];
const MARKDOWN_EXTENSIONS = ['md', 'markdown'];
const CSV_EXTENSIONS = ['csv', 'tsv'];

/**
 * Plain-text and source files rendered verbatim in a <pre>. Deliberately wider
 * than source-types' TEXT_EXTENSIONS, which only covers prose extensions
 * (txt/rtf/md/markdown) and so left every code artifact without a preview.
 */
const TEXT_EXTENSIONS = [
  'txt',
  'rtf',
  'py',
  'js',
  'jsx',
  'ts',
  'tsx',
  'css',
  'scss',
  'less',
  'yaml',
  'yml',
  'xml',
  'sql',
  'sh',
  'bash',
  'zsh',
  'log',
  'toml',
  'ini',
  'cfg',
  'conf',
  'env',
  'jsonl',
  'ndjson',
  'java',
  'go',
  'rb',
  'rs',
  'c',
  'h',
  'cpp',
  'hpp',
  'cs',
  'php',
  'swift',
  'kt',
  'r',
  'pl',
  'lua',
  'dockerfile',
  'makefile',
  'gitignore',
];

const MAX_CSV_PREVIEW_ROWS = 5000;

function getExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() ?? '';
}

/**
 * Resolve how a file should be previewed. `artifactType` is an optional hint
 * from the artifact record, which catches files the backend typed correctly but
 * whose name and mime are uninformative (e.g. a web page served as
 * application/octet-stream).
 */
function categorizeFile(
  file: ArtifactFile | null,
  artifactType?: ArtifactTypeHint,
): FileCategory {
  if (!file) return 'none';
  const ext = getExtension(file.name);
  const mime = file.mime_type?.toLowerCase() ?? '';

  // Office first: xlsx would otherwise be caught by the spreadsheet/csv rules.
  if (PRESENTATION_EXTENSIONS.includes(ext)) return 'office-ppt';
  if (DOCUMENT_EXTENSIONS.includes(ext)) return 'office-doc';
  if (EXCEL_EXTENSIONS.includes(ext)) return 'office-xls';

  if (ext === 'pdf' || mime === 'application/pdf') return 'pdf';
  if (
    ext === 'html' ||
    ext === 'htm' ||
    mime.includes('html') ||
    artifactType === 'web_page'
  ) {
    return 'html';
  }
  if (CSV_EXTENSIONS.includes(ext) || mime === 'text/csv') return 'csv';
  if (ext === 'json' || mime === 'application/json') return 'json';
  if (MARKDOWN_EXTENSIONS.includes(ext) || mime === 'text/markdown') {
    return 'markdown';
  }

  if (
    IMAGE_EXTENSIONS.includes(ext) ||
    artifactType === 'image' ||
    mime.startsWith('image/')
  ) {
    return 'image';
  }
  if (
    VIDEO_EXTENSIONS.includes(ext) ||
    artifactType === 'video' ||
    mime.startsWith('video/')
  ) {
    return 'video';
  }
  if (
    AUDIO_EXTENSIONS.includes(ext) ||
    artifactType === 'audio' ||
    mime.startsWith('audio/')
  ) {
    return 'audio';
  }

  if (TEXT_EXTENSIONS.includes(ext) || mime.startsWith('text/')) return 'text';

  return 'none';
}

// ---------------------------------------------------------------------------
// FileRenderer
// ---------------------------------------------------------------------------

interface FileRendererProps {
  file: ArtifactFile;
  /** Freshly re-signed URLs for `file`, when the caller has refreshed them. */
  refreshedFile?: NodeFile | null;
  onDownload?: () => void;
  isDownloading?: boolean;
  artifactType?: ArtifactTypeHint;
}

export function FileRenderer({
  file,
  refreshedFile = null,
  onDownload,
  isDownloading = false,
  artifactType,
}: FileRendererProps) {
  const previewUrl =
    refreshedFile?.proxy_url ||
    refreshedFile?.presigned_url ||
    file.proxy_url ||
    file.presigned_url ||
    '';
  const proxyUrl = refreshedFile?.proxy_url || file.proxy_url || '';

  const rawCategory = previewUrl ? categorizeFile(file, artifactType) : 'none';
  // PDF and HTML embed via iframe: a raw presigned S3 URL often can't be
  // framed (CORS/X-Frame-Options), so these two require the proxied URL and
  // fall back to the download UI otherwise.
  const needsProxyUrl = rawCategory === 'pdf' || rawCategory === 'html';
  const category = needsProxyUrl && !proxyUrl ? 'none' : rawCategory;

  const fallback = (
    <ViewerFallback
      title={file.name}
      url={previewUrl || null}
      onDownload={onDownload}
      isDownloading={isDownloading}
    />
  );

  switch (category) {
    case 'image':
      return (
        <div className='flex h-full w-full items-center justify-center overflow-auto bg-accent/20 p-4'>
          <img
            data-testid='artifact-viewer-image'
            src={previewUrl}
            alt={file.name}
            className='max-h-full max-w-full rounded-lg object-contain shadow-lg'
          />
        </div>
      );

    case 'video':
      return (
        <div
          data-testid='artifact-viewer-video'
          className='flex h-full items-center justify-center p-8'
        >
          <div className='w-full max-w-4xl'>
            <VideoPlayer videoDataUrl={previewUrl} />
          </div>
        </div>
      );

    case 'audio':
      return (
        <div
          data-testid='artifact-viewer-audio'
          className='flex h-full items-center justify-center p-8'
        >
          <div className='w-full max-w-xl'>
            <AudioPlayer audioDataUrl={previewUrl} download audioBar />
          </div>
        </div>
      );

    case 'office-ppt':
      return (
        <div data-testid='artifact-viewer-office' className='h-full w-full'>
          <PowerPointDocumentDisplay data={previewUrl} />
        </div>
      );

    case 'office-doc':
      return (
        <div data-testid='artifact-viewer-office' className='h-full w-full'>
          <WordDocumentDisplay data={previewUrl} />
        </div>
      );

    case 'office-xls':
      return (
        <div data-testid='artifact-viewer-office' className='h-full w-full'>
          <ExcelDocumentDisplay data={previewUrl} />
        </div>
      );

    case 'pdf':
      return (
        <iframe
          data-testid='artifact-viewer-pdf'
          src={proxyUrl}
          title={file.name}
          className='h-full w-full border-0'
        />
      );

    case 'html':
      return (
        <iframe
          data-testid='artifact-viewer-web-page'
          src={proxyUrl}
          title={file.name}
          className='h-full w-full border-0 bg-white'
          sandbox={`${AGENT_HTML_IFRAME_SANDBOX} allow-downloads`}
        />
      );

    case 'markdown':
      return (
        <MarkdownFileViewer file={file} url={previewUrl} fallback={fallback} />
      );

    case 'csv':
      return <CsvFileViewer file={file} url={previewUrl} fallback={fallback} />;

    case 'json':
      return (
        <JsonFileViewer file={file} url={previewUrl} fallback={fallback} />
      );

    case 'text':
      return (
        <TextFileViewer file={file} url={previewUrl} fallback={fallback} />
      );

    default:
      return fallback;
  }
}

// ---------------------------------------------------------------------------
// Text-backed viewers
// ---------------------------------------------------------------------------

function useArtifactFileText(url: string, fileName: string) {
  const [content, setContent] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setContent(null);
    setFailed(false);

    // Agentic-backend proxy URLs are cross-origin for the browser; rewrite
    // them through /proxy/workflow or the fetch dies on CORS. The iframe and
    // img viewers don't care, but these viewers use fetch().
    const fetchUrl = toSameOriginWorkflowUrl(url);
    if (!isSafeFetchUrl(fetchUrl)) {
      Sentry.captureException(
        new Error('Refusing to fetch an unsafe file URL'),
        {
          extra: { context: 'artifact-text-viewer', fileName },
        },
      );
      setFailed(true);
      return;
    }

    fetch(fetchUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load document (${res.status})`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setContent(text);
      })
      .catch((err) => {
        Sentry.captureException(err, {
          extra: { context: 'artifact-text-viewer', fileName },
        });
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [url, fileName]);

  return { content, failed };
}

function ViewerLoading() {
  return (
    <div className='flex h-full w-full items-center justify-center text-muted-foreground'>
      <Loader2 className='size-5 animate-spin' />
    </div>
  );
}

interface TextBackedViewerProps {
  file: ArtifactFile;
  url: string;
  fallback: React.ReactNode;
}

function MarkdownFileViewer({ file, url, fallback }: TextBackedViewerProps) {
  const { content, failed } = useArtifactFileText(url, file.name);

  if (failed) return fallback;
  if (content === null) return <ViewerLoading />;

  return (
    <div
      data-testid='artifact-viewer-markdown'
      className='h-full w-full overflow-y-auto px-8 py-6'
    >
      <div className='mx-auto max-w-3xl'>
        <MarkdownRenderer content={content} />
      </div>
    </div>
  );
}

function CsvFileViewer({ file, url, fallback }: TextBackedViewerProps) {
  const { content, failed } = useArtifactFileText(url, file.name);

  const parsed = useMemo(() => {
    if (content === null) return null;
    const result = Papa.parse<Record<string, string | null>>(content, {
      header: true,
      skipEmptyLines: true,
    });
    const fields = result.meta.fields ?? [];
    const columns = fields.map((field, index) => ({
      key: `col_${index}`,
      title: field && field.trim().length ? field : `Column ${index + 1}`,
    }));
    const rows = (result.data ?? [])
      .slice(0, MAX_CSV_PREVIEW_ROWS)
      .map((row) => {
        const mapped: Record<string, string | null> = {};
        fields.forEach((field, index) => {
          const value = row[field] ?? null;
          mapped[`col_${index}`] = value === '' ? null : value;
        });
        return mapped;
      });
    return { columns, rows, truncated: result.data.length > rows.length };
  }, [content]);

  if (failed) return fallback;
  if (parsed === null) return <ViewerLoading />;
  if (parsed.columns.length === 0) return fallback;

  return (
    <div
      data-testid='artifact-viewer-csv'
      className='flex h-full w-full flex-col'
    >
      <div className='min-h-0 flex-1 overflow-auto'>
        <Suspense fallback={<ViewerLoading />}>
          <CsvPreviewGrid columns={parsed.columns} rows={parsed.rows} />
        </Suspense>
      </div>
      <div className='border-t px-4 py-2 text-xs text-muted-foreground'>
        {parsed.rows.length.toLocaleString()} row
        {parsed.rows.length === 1 ? '' : 's'}
        {parsed.truncated &&
          ` · preview limited to first ${MAX_CSV_PREVIEW_ROWS.toLocaleString()}`}
      </div>
    </div>
  );
}

function JsonFileViewer({ file, url, fallback }: TextBackedViewerProps) {
  const { content, failed } = useArtifactFileText(url, file.name);

  const formatted = useMemo(() => {
    if (content === null) return null;
    try {
      return JSON.stringify(JSON.parse(content), null, 2);
    } catch {
      return content;
    }
  }, [content]);

  if (failed) return fallback;
  if (formatted === null) return <ViewerLoading />;

  return (
    <pre
      data-testid='artifact-viewer-json'
      className='h-full w-full overflow-auto p-6 font-mono text-xs leading-relaxed'
    >
      {formatted}
    </pre>
  );
}

function TextFileViewer({ file, url, fallback }: TextBackedViewerProps) {
  const { content, failed } = useArtifactFileText(url, file.name);

  if (failed) return fallback;
  if (content === null) return <ViewerLoading />;

  return (
    <pre
      data-testid='artifact-viewer-text'
      className='h-full w-full overflow-auto whitespace-pre-wrap break-words p-6 font-mono text-xs leading-relaxed'
    >
      {content}
    </pre>
  );
}

// ---------------------------------------------------------------------------
// Fallback
// ---------------------------------------------------------------------------

function ViewerFallback({
  title,
  url,
  onDownload,
  isDownloading = false,
}: {
  title: string;
  url: string | null;
  onDownload?: () => void;
  isDownloading?: boolean;
}) {
  return (
    <div
      data-testid='artifact-viewer-fallback'
      className='flex h-full w-full flex-col items-center justify-center gap-3 p-8 text-center'
    >
      <FileWarning className='size-8 text-muted-foreground' />
      <p className='max-w-sm text-sm text-muted-foreground'>
        {url
          ? 'This artifact type has no inline preview.'
          : 'This artifact has no viewable file.'}
      </p>
      {onDownload ? (
        <Button variant='outline' onClick={onDownload} disabled={isDownloading}>
          <Download className='mr-2 size-4' />
          {isDownloading ? 'Downloading…' : 'Download'}
        </Button>
      ) : (
        url && (
          <Button variant='outline' size='sm' asChild>
            <a href={url} target='_blank' rel='noopener noreferrer'>
              <Download className='size-4' />
              Open {title}
            </a>
          </Button>
        )
      )}
    </div>
  );
}
